import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import API_BASE, { protectedApiFetch, withApiCredentials } from "../../api";
import useLocationStateStack from "../../app/useLocationStateStack";
import { getActiveAwareAuthToken } from "../../auth/bootstrapTokens";
import { buildShareCountLookup } from "../../utils/postShares";
import useOrdinaryPostLikeMutation from "../../hooks/useOrdinaryPostLikeMutation";
import usePaginatedUserPosts from "../../hooks/usePaginatedUserPosts";

function asNonNegativeNumber(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
}

function normalizeTimestampToMillis(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed < 1e12 ? parsed * 1000 : parsed;
}

function compareUsersByPublicRanking(leftUser, rightUser) {
  const xpDifference =
    asNonNegativeNumber(rightUser?.progression?.xp) -
    asNonNegativeNumber(leftUser?.progression?.xp);

  if (xpDifference !== 0) {
    return xpDifference;
  }

  const leftCreatedAt = normalizeTimestampToMillis(leftUser?.createdAt);
  const rightCreatedAt = normalizeTimestampToMillis(rightUser?.createdAt);

  if (leftCreatedAt !== rightCreatedAt) {
    if (leftCreatedAt === null) return 1;
    if (rightCreatedAt === null) return -1;
    return leftCreatedAt - rightCreatedAt;
  }

  return String(leftUser?.username || "").localeCompare(
    String(rightUser?.username || ""),
    undefined,
    { sensitivity: "base" }
  );
}

function normalizePublicUserIdentity(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

function isSamePublicRankedUser(leftUser, rightUser) {
  if (!leftUser || !rightUser) return false;

  const leftId = normalizePublicUserIdentity(leftUser.id || leftUser.user_id);
  const rightId = normalizePublicUserIdentity(rightUser.id || rightUser.user_id);
  if (leftId && rightId && leftId === rightId) return true;

  const leftEmail = normalizePublicUserIdentity(leftUser.email);
  const rightEmail = normalizePublicUserIdentity(rightUser.email);
  if (leftEmail && rightEmail && leftEmail === rightEmail) return true;

  const leftUsername = normalizePublicUserIdentity(leftUser.username);
  const rightUsername = normalizePublicUserIdentity(rightUser.username);
  return Boolean(leftUsername && rightUsername && leftUsername === rightUsername);
}

function blockRequestErrorMessage(t, status) {
  if (status === 401 || status === 403) {
    return t("user_profile_block_auth_error", {
      defaultValue: "Please sign in again to manage this setting.",
    });
  }
  if (status === 404) {
    return t("user_profile_block_not_found", {
      defaultValue: "This user is no longer available.",
    });
  }
  if (status === 409) {
    return t("user_profile_block_conflict", {
      defaultValue: "This action is not available.",
    });
  }
  return t("user_profile_block_unavailable", {
    defaultValue: "Could not update this setting right now. Please try again.",
  });
}

function attachPublicLeaderboardRank(profileUser, users) {
  if (!profileUser || typeof profileUser !== "object") return profileUser;

  const rankedUsers = Array.isArray(users)
    ? [...users].sort(compareUsersByPublicRanking)
    : [];
  const rankIndex = rankedUsers.findIndex((rankedUser) =>
    isSamePublicRankedUser(rankedUser, profileUser)
  );
  const rankPosition = rankIndex >= 0 ? rankIndex + 1 : null;
  const progression =
    profileUser.progression &&
    typeof profileUser.progression === "object" &&
    !Array.isArray(profileUser.progression)
      ? { ...profileUser.progression }
      : profileUser.progression;

  if (!rankPosition) {
    return {
      ...profileUser,
      progression,
    };
  }

  return {
    ...profileUser,
    globalRankPosition: rankPosition,
    progression:
      progression && typeof progression === "object" && !Array.isArray(progression)
        ? {
            ...progression,
            globalRankPosition: rankPosition,
          }
        : {
            globalRankPosition: rankPosition,
          },
  };
}

export default function useUserProfileState({
  user,
  selectedUser,
  focusPostRequest,
  setShowUserProfile,
  setSelectedPost,
  setShowInfo,
  setChatUserId,
  setSharePost,
}) {
  const { t } = useTranslation();
  const backLabel = t("back", { defaultValue: "Back" }).replace(
    /^[<\-\u2013\u2014\u2190\u2B05\s]+/,
    ""
  );

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [expandedMedia, setExpandedMedia] = useState(null);
  const [openComments, setOpenComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [usersMap, setUsersMap] = useState({});
  const [highlightedCommentKey, setHighlightedCommentKey] = useState(null);
  const [friendStatus, setFriendStatus] = useState("loading");
  const [friendRequestPending, setFriendRequestPending] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followAllowed, setFollowAllowed] = useState(true);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followActionPending, setFollowActionPending] = useState(false);
  const [blockStatus, setBlockStatus] = useState("idle");
  const [hasBlockedUser, setHasBlockedUser] = useState(false);
  const [blockActionPending, setBlockActionPending] = useState(false);
  const [blockFeedback, setBlockFeedback] = useState("");
  const [blockError, setBlockError] = useState("");
  const { togglePostLike, isPostLikePending } =
    useOrdinaryPostLikeMutation();
  const profilePostPagination = usePaginatedUserPosts(selectedUserId);
  const [confirmAction, setConfirmAction] = useState(null);

  const postRefs = useRef({});
  const tRef = useRef(t);
  const commentRefs = useRef({});
  const previousOpenCommentsRef = useRef(null);
  const usersMapRef = useRef({});
  const usersDirectoryRequestRef = useRef(null);

  tRef.current = t;

  const focusPostId = focusPostRequest?.postId;
  const focusCommentId = focusPostRequest?.commentId;
  const focusPostRequestedAt = focusPostRequest?.requestedAt;
  const authReadToken = getActiveAwareAuthToken(window);
  const authMutationToken = authReadToken;
  const userPosts = profilePostPagination.posts;
  const shareCountLookup = buildShareCountLookup(userPosts);

  const closeConfirmModal = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const handleCloseExpandedMedia = useCallback(() => {
    setExpandedMedia(null);
  }, []);

  const userProfileHistoryItems = [
    {
      id: "user-profile-expanded-media",
      isOpen: Boolean(expandedMedia),
      close: handleCloseExpandedMedia,
    },
    {
      id: "user-profile-confirm",
      isOpen: Boolean(confirmAction),
      close: closeConfirmModal,
    },
  ];

  useLocationStateStack("userProfileOverlayStack", userProfileHistoryItems);

  const resetCommentDraftState = useCallback(() => {
    setCommentText("");
    setReplyTo(null);
    setReplyText("");
    setHighlightedCommentKey(null);
  }, []);

  const resetCommentThreadState = useCallback(() => {
    setOpenComments(null);
    resetCommentDraftState();
    commentRefs.current = {};
  }, [resetCommentDraftState]);

  useEffect(() => {
    if (expandedMedia) {
      document.body.classList.add("media-open");
    } else {
      document.body.classList.remove("media-open");
    }

    return () => {
      document.body.classList.remove("media-open");
    };
  }, [expandedMedia]);

  useEffect(() => {
    if (!focusPostId) return;

    const timeoutId = window.setTimeout(() => {
      const targetNode = postRefs.current?.[focusPostId];
      if (!targetNode) return;
      targetNode.scrollIntoView({ behavior: "smooth", block: "center" });
      targetNode.focus?.({ preventScroll: true });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [focusPostId, focusPostRequestedAt, userPosts]);

  useEffect(() => {
    if (!focusPostId || !focusCommentId) return;

    setOpenComments(focusPostId);
    setReplyTo(null);
    setReplyText("");
  }, [focusPostId, focusCommentId, focusPostRequestedAt]);

  useEffect(() => {
    resetCommentThreadState();
    previousOpenCommentsRef.current = null;
  }, [selectedUser, resetCommentThreadState]);

  useEffect(() => {
    if (previousOpenCommentsRef.current !== openComments) {
      resetCommentDraftState();
      previousOpenCommentsRef.current = openComments;
    }
  }, [openComments, resetCommentDraftState]);

  useEffect(() => {
    if (!focusPostId || !focusCommentId || openComments !== focusPostId) {
      return undefined;
    }

    const commentKey = `${focusPostId}:${focusCommentId}`;
    let retryTimeoutId = null;
    let animationFrameId = null;
    let clearHighlightTimeoutId = null;
    let attempts = 0;
    let cancelled = false;

    const focusTargetComment = () => {
      if (cancelled) return;

      const targetNode = commentRefs.current?.[commentKey];
      if (!targetNode) {
        if (attempts >= 20) return;
        attempts += 1;
        retryTimeoutId = window.setTimeout(() => {
          animationFrameId = window.requestAnimationFrame(focusTargetComment);
        }, 80);
        return;
      }

      targetNode.scrollIntoView({ behavior: "smooth", block: "center" });
      targetNode.focus?.({ preventScroll: true });
      setHighlightedCommentKey(commentKey);

      clearHighlightTimeoutId = window.setTimeout(() => {
        setHighlightedCommentKey((current) =>
          current === commentKey ? null : current
        );
      }, 2500);
    };

    animationFrameId = window.requestAnimationFrame(focusTargetComment);

    return () => {
      cancelled = true;
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
      }
      if (clearHighlightTimeoutId) {
        window.clearTimeout(clearHighlightTimeoutId);
      }
    };
  }, [focusPostId, focusCommentId, focusPostRequestedAt, openComments, userPosts]);

  useEffect(() => {
    if (!selectedUser) return;
    if (user && selectedUser === user.username) {
      setShowUserProfile(false);
    }
  }, [selectedUser, user, setShowUserProfile]);

  useEffect(() => {
    usersMapRef.current = usersMap;
  }, [usersMap]);

  const loadUsersDirectory = useCallback(async () => {
    if (!usersDirectoryRequestRef.current) {
      usersDirectoryRequestRef.current = fetch(`${API_BASE}/auth/users`, {
        cache: "no-store",
      })
        .then(async (res) => {
          if (!res.ok) return null;
          return res.json();
        })
        .finally(() => {
          usersDirectoryRequestRef.current = null;
        });
    }

    return usersDirectoryRequestRef.current;
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadUsers() {
      try {
        const users = await loadUsersDirectory();
        if (!users) return;
        const map = {};
        users.forEach((loadedUser) => {
          map[loadedUser.id] = loadedUser;
        });
        if (isMounted) {
          setUsersMap(map);
        }
      } catch (err) {
        console.error("loadUsers:", err);
      }
    }

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [loadUsersDirectory]);

  useEffect(() => {
    let isMounted = true;

    async function resolveSelectedUserId() {
      if (!selectedUser) return;

      try {
        setSelectedUserId(null);
        setFriendStatus("loading");
        setSelectedUserData(null);
        setFollowersCount(0);
        setFollowingCount(0);

        const localUsers = Object.values(usersMapRef.current || {});
        const foundInLocalMap = localUsers.find(
          (loadedUser) => loadedUser.username === selectedUser
        );

        if (foundInLocalMap?.id) {
          if (isMounted) {
            setSelectedUserId(foundInLocalMap.id);
            setSelectedUserData(
              attachPublicLeaderboardRank(foundInLocalMap, localUsers)
            );
            setFollowersCount(Number(foundInLocalMap.followersCount) || 0);
            setFollowingCount(Number(foundInLocalMap.followingCount) || 0);
          }
          return;
        }

        const users = await loadUsersDirectory();
        if (!users) throw new Error("Failed to load users");
        const found = users.find((loadedUser) => loadedUser.username === selectedUser);

        if (!found?.id) throw new Error("Selected user not found");

        if (isMounted) {
          setSelectedUserId(found.id);
          setSelectedUserData(attachPublicLeaderboardRank(found, users));
          setFollowersCount(Number(found.followersCount) || 0);
          setFollowingCount(Number(found.followingCount) || 0);
        }
      } catch (err) {
        console.error("resolveSelectedUserId:", err);
        if (isMounted) {
          setSelectedUserId(null);
          setSelectedUserData(null);
          setFriendStatus("none");
          setFollowersCount(0);
          setFollowingCount(0);
        }
      }
    }

    resolveSelectedUserId();

    return () => {
      isMounted = false;
    };
  }, [loadUsersDirectory, selectedUser]);

  useEffect(() => {
    let isMounted = true;

    async function loadFollowStatus() {
      if (!selectedUserId) return;

      try {
        const res = await fetch(
          `${API_BASE}/auth/me`,
          withApiCredentials({
            headers: authReadToken
              ? {
                  Authorization: `Bearer ${authReadToken}`,
                }
              : {},
          })
        );
        if (!res.ok) throw new Error("Failed to load me");

        const me = await res.json();
        const friends = me.friends || [];
        const sent = me.friendRequestsSent || [];
        const received = me.friendRequestsReceived || [];

        if (isMounted) {
          setIsFollowing(me.following?.includes(selectedUserId));
        }

        if (!isMounted) return;

        if (selectedUserData?.followPrivacy === "nobody") setFollowAllowed(false);
        else if (
          selectedUserData?.followPrivacy === "friends" &&
          !friends.includes(selectedUserId)
        )
          setFollowAllowed(false);
        else setFollowAllowed(true);

        let next = "none";
        if (friends.includes(selectedUserId)) next = "friends";
        else if (sent.includes(selectedUserId)) next = "requested";
        else if (received.includes(selectedUserId)) next = "received";

        setFriendStatus(next);
      } catch (err) {
        console.error("loadFollowStatus:", err);
        if (isMounted) setFriendStatus("none");
      }
    }

    loadFollowStatus();
    return () => {
      isMounted = false;
    };
  }, [authReadToken, selectedUserData?.followPrivacy, selectedUserId]);

  useEffect(() => {
    const controller = new AbortController();
    setHasBlockedUser(false);
    setBlockFeedback("");
    setBlockError("");

    if (!selectedUserId || selectedUserId === user?.id) {
      setBlockStatus("idle");
      return () => controller.abort();
    }

    let active = true;
    setBlockStatus("loading");

    async function loadBlockStatus() {
      try {
        const response = await protectedApiFetch(
          `${API_BASE}/user-blocks/status`,
          {
            method: "POST",
            cache: "no-store",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ targetUserId: selectedUserId }),
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          if (active) {
            setBlockStatus("unavailable");
            setBlockError(
              blockRequestErrorMessage(tRef.current, response.status)
            );
          }
          return;
        }
        const payload = await response.json();
        if (typeof payload?.blocked !== "boolean") {
          throw new Error("block_status_contract_invalid");
        }
        if (active) {
          setHasBlockedUser(payload.blocked);
          setBlockStatus("ready");
        }
      } catch (error) {
        if (!active || error?.name === "AbortError") return;
        setBlockStatus("unavailable");
        setBlockError(blockRequestErrorMessage(tRef.current));
      }
    }

    loadBlockStatus();
    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedUserId, user?.id]);

  useEffect(() => {
    const handleClose = () => {
      setShowUserProfile(false);
    };

    window.addEventListener("closeUserProfile", handleClose);

    return () => {
      window.removeEventListener("closeUserProfile", handleClose);
    };
  }, [setShowUserProfile]);

  const sendFriendRequest = async () => {
    if (!selectedUserId || friendRequestPending) {
      return false;
    }

    const previousFriendStatus = friendStatus;
    setFriendRequestPending(true);

    try {
      const formData = new FormData();
      if (authMutationToken) {
        formData.append("token", authMutationToken);
      }
      formData.append("target_user_id", selectedUserId);

      const res = await fetch(
        `${API_BASE}/friends/request`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) throw new Error("request failed");

      setFriendStatus("requested");
      return true;
    } catch (err) {
      setFriendStatus(previousFriendStatus);
      console.error("sendFriendRequest:", err);
      return false;
    } finally {
      setFriendRequestPending(false);
    }
  };

  const acceptFriendRequest = async () => {
    if (!selectedUserId) return;

    try {
      const formData = new FormData();
      if (authMutationToken) {
        formData.append("token", authMutationToken);
      }
      formData.append("from_user_id", selectedUserId);

      const res = await fetch(
        `${API_BASE}/friends/accept`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) throw new Error("accept failed");

      setFriendStatus("friends");
    } catch (err) {
      console.error("acceptFriendRequest:", err);
    }
  };

  const declineFriendRequest = async () => {
    if (!selectedUserId) return;

    try {
      const formData = new FormData();
      if (authMutationToken) {
        formData.append("token", authMutationToken);
      }
      formData.append("from_user_id", selectedUserId);

      const res = await fetch(
        `${API_BASE}/friends/decline`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) throw new Error("decline failed");

      setFriendStatus("none");
    } catch (err) {
      console.error("declineFriendRequest:", err);
    }
  };

  const cancelFriendRequest = async () => {
    if (!selectedUserId || friendRequestPending) {
      return false;
    }

    const previousFriendStatus = friendStatus;
    setFriendRequestPending(true);

    try {
      const formData = new FormData();
      if (authMutationToken) {
        formData.append("token", authMutationToken);
      }
      formData.append("target_user_id", selectedUserId);

      const res = await fetch(
        `${API_BASE}/friends/cancel`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) throw new Error("cancel failed");

      setFriendStatus("none");
      return true;
    } catch (err) {
      setFriendStatus(previousFriendStatus);
      console.error("cancelFriendRequest:", err);
      return false;
    } finally {
      setFriendRequestPending(false);
    }
  };

  const removeFriend = async () => {
    if (!selectedUserId) return;

    try {
      const formData = new FormData();
      if (authMutationToken) {
        formData.append("token", authMutationToken);
      }
      formData.append("target_user_id", selectedUserId);

      const res = await fetch(
        `${API_BASE}/friends/remove`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) throw new Error("remove friend failed");

      setFriendStatus("none");
      setSelectedUserData((current) =>
        current
          ? {
              ...current,
              friends: (current.friends || []).filter((id) => id !== user?.id),
            }
          : current
      );

      if (!isFollowing && selectedUserData?.followPrivacy === "friends") {
        setFollowAllowed(false);
      }
    } catch (err) {
      console.error("removeFriend:", err);
    }
  };

  const syncSelectedUserFollowers = (nextIsFollowing, currentFollowers) => {
    if (!Array.isArray(currentFollowers) || !user?.id) {
      return currentFollowers;
    }

    if (nextIsFollowing) {
      return currentFollowers.includes(user.id)
        ? currentFollowers
        : [...currentFollowers, user.id];
    }

    return currentFollowers.filter((id) => id !== user.id);
  };

  const restoreFollowSnapshot = (snapshot) => {
    setIsFollowing(snapshot.isFollowing);
    setFollowersCount(snapshot.followersCount);
    setSelectedUserData((current) =>
      current
        ? {
            ...current,
            followers: snapshot.selectedUserFollowers,
          }
        : current
    );
  };

  const runFollowMutation = async ({ endpoint, nextIsFollowing, actionName }) => {
    if (!selectedUserId || followActionPending) {
      return false;
    }

    const snapshot = {
      isFollowing,
      followersCount,
      selectedUserFollowers: Array.isArray(selectedUserData?.followers)
        ? [...selectedUserData.followers]
        : selectedUserData?.followers,
    };

    setFollowActionPending(true);

    try {
      const fd = new FormData();
      if (authMutationToken) {
        fd.append("token", authMutationToken);
      }
      fd.append("target_user_id", selectedUserId);

      const res = await fetch(
        `${API_BASE}${endpoint}`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );

      if (!res.ok) {
        throw new Error(`${actionName} failed`);
      }

      setIsFollowing(nextIsFollowing);
      setFollowersCount((current) =>
        nextIsFollowing ? current + 1 : Math.max(0, current - 1)
      );
      setSelectedUserData((current) =>
        current
          ? {
              ...current,
              followers: syncSelectedUserFollowers(
                nextIsFollowing,
                current.followers
              ),
            }
          : current
      );
      notifySocialGraphUpdated();
      return true;
    } catch (err) {
      restoreFollowSnapshot(snapshot);
      console.error(`follow mutation failed (${actionName}):`, err);
      return false;
    } finally {
      setFollowActionPending(false);
    }
  };

  const followUser = async () =>
    runFollowMutation({
      endpoint: "/follow",
      nextIsFollowing: true,
      actionName: "follow",
    });

  const unfollowUser = async () =>
    runFollowMutation({
      endpoint: "/follow/unfollow",
      nextIsFollowing: false,
      actionName: "unfollow",
    });

  const handleFollowButtonClick = () => {
    if (followActionPending) return;

    if (isFollowing) {
      setConfirmAction("unfollow");
      return;
    }

    followUser();
  };

  const handleFriendButtonClick = () => {
    setConfirmAction("unfriend");
  };

  const runBlockMutation = async (nextBlocked) => {
    if (
      !selectedUserId ||
      selectedUserId === user?.id ||
      blockStatus !== "ready" ||
      blockActionPending
    ) {
      return false;
    }

    setBlockActionPending(true);
    setBlockError("");
    setBlockFeedback("");
    try {
      const response = await protectedApiFetch(
        `${API_BASE}/user-blocks/${nextBlocked ? "block" : "unblock"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetUserId: selectedUserId }),
        }
      );
      if (!response.ok) {
        setBlockError(blockRequestErrorMessage(t, response.status));
        return false;
      }
      const payload = await response.json();
      if (payload?.blocked !== nextBlocked) {
        setBlockError(blockRequestErrorMessage(t));
        return false;
      }

      setHasBlockedUser(nextBlocked);
      setBlockFeedback(
        nextBlocked
          ? t("user_profile_block_success", {
              defaultValue: "User blocked.",
            })
          : t("user_profile_unblock_success", {
              defaultValue: "User unblocked.",
            })
      );
      return true;
    } catch {
      setBlockError(blockRequestErrorMessage(t));
      return false;
    } finally {
      setBlockActionPending(false);
    }
  };

  const handleBlockButtonClick = () => {
    if (blockActionPending || blockStatus !== "ready") return;
    if (hasBlockedUser) {
      runBlockMutation(false);
      return;
    }
    setConfirmAction("block");
  };

  const handleConfirmAction = async () => {
    if (confirmAction === "unfriend") {
      await removeFriend();
    } else if (confirmAction === "unfollow") {
      const success = await unfollowUser();
      if (!success) return;
    } else if (confirmAction === "block") {
      const success = await runBlockMutation(true);
      if (!success) return;
    }

    setConfirmAction(null);
  };

  const handleCloseUserProfile = () => {
    resetCommentThreadState();
    setExpandedMedia(null);
    setConfirmAction(null);
    setShowUserProfile(false);
  };

  const notifySocialGraphUpdated = () => {
    window.dispatchEvent(new Event("friendsUpdated"));
    window.dispatchEvent(new Event("postsUpdated"));
  };

  const handleMessageClick = () => {
    if (!selectedUserId) return;
    setShowUserProfile(false);
    setChatUserId({
      userId: selectedUserId,
      userName: selectedUserData?.username || selectedUser || "",
    });
  };

  const handleOpenExpandedMedia = (payload) => {
    setExpandedMedia(payload);
  };

  const handleLikePost = (post) =>
    togglePostLike({
      post,
      actorUserId: user?.id,
      errorMessage: t("post_like_update_failed", {
        defaultValue: "Could not update the like. Please try again.",
      }),
    });

  const handleToggleComments = (postId) => {
    const next = openComments === postId ? null : postId;
    setOpenComments(next);
  };

  const handleOpenShare = (post) => {
    setSharePost(post);
  };

  const handleOpenInfo = (post) => {
    setSelectedPost(post);
    setShowInfo(true);
  };

  const handleLikeComment = async (postId, commentId) => {
    const localToken = localStorage.getItem("token");

    try {
      const fd = new FormData();
      if (localToken) {
        fd.append("token", localToken);
      }
      fd.append("post_id", postId);
      fd.append("comment_id", commentId);

      const res = await fetch(
        `${API_BASE}/posts/comment/like`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );
      if (!res.ok) throw new Error("comment like failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("commentLiked", {
          detail: {
            postId,
            commentId,
            likes: data.likes,
          },
        })
      );
    } catch (err) {
      console.error("comment like:", err);
    }
  };

  const handleToggleReply = (commentId) => {
    setReplyTo((current) => {
      const next = current === commentId ? null : commentId;
      setReplyText("");
      return next;
    });
  };

  const handleLikeReply = async (postId, commentId, replyId) => {
    const localToken = localStorage.getItem("token");

    try {
      const fd = new FormData();
      if (localToken) {
        fd.append("token", localToken);
      }
      fd.append("post_id", postId);
      fd.append("comment_id", commentId);
      fd.append("reply_id", replyId);

      const res = await fetch(
        `${API_BASE}/posts/comment/reply/like`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );

      if (!res.ok) throw new Error("reply like failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("replyLiked", {
          detail: {
            postId,
            commentId,
            replyId,
            likes: data.likes,
          },
        })
      );
    } catch (err) {
      console.error("reply like:", err);
    }
  };

  const handleSubmitReply = async (postId, commentId) => {
    const localToken = localStorage.getItem("token");
    if (!replyText.trim()) return;

    try {
      const fd = new FormData();
      if (localToken) {
        fd.append("token", localToken);
      }
      fd.append("post_id", postId);
      fd.append("comment_id", commentId);
      fd.append("text", replyText);

      const res = await fetch(
        `${API_BASE}/posts/comment/reply`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );

      if (!res.ok) throw new Error("reply failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("commentReplied", {
          detail: {
            postId,
            commentId,
            reply: data.reply,
          },
        })
      );

      setReplyText("");
      setReplyTo(null);
    } catch (err) {
      console.error("reply:", err);
    }
  };

  const handleSubmitComment = async (postId) => {
    const localToken = localStorage.getItem("token");
    if (!commentText.trim()) return;

    try {
      const fd = new FormData();
      if (localToken) {
        fd.append("token", localToken);
      }
      fd.append("post_id", postId);
      fd.append("text", commentText);

      const res = await fetch(
        `${API_BASE}/posts/comment`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );

      if (!res.ok) throw new Error("comment failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("postCommented", {
          detail: {
            postId,
            comment: data.comment,
          },
        })
      );

      setCommentText("");
      setOpenComments(null);
      setReplyTo(null);
      setReplyText("");
    } catch (err) {
      console.error("comment:", err);
    }
  };

  const confirmMessage =
    confirmAction === "unfriend"
      ? t("user_profile_confirm_unfriend")
      : confirmAction === "unfollow"
        ? t("user_profile_confirm_unfollow")
        : confirmAction === "block"
          ? t("user_profile_confirm_block", {
              defaultValue:
                "Block this user? They will not be notified about this action.",
            })
        : "";

  const canManageBlock = Boolean(
    selectedUserId && user?.id && selectedUserId !== user.id
  );
  const confirmActionPending =
    confirmAction === "block" ? blockActionPending : followActionPending;
  const confirmError = confirmAction === "block" ? blockError : "";

  const friendsCount = Number(selectedUserData?.friendsCount) || 0;

  return {
    t,
    user,
    selectedUser,
    selectedUserId,
    selectedUserData,
    expandedMedia,
    openComments,
    commentText,
    replyTo,
    replyText,
    usersMap,
    highlightedCommentKey,
    friendStatus,
    friendRequestPending,
    isFollowing,
    followAllowed,
    followersCount,
    followingCount,
    followActionPending,
    blockStatus,
    hasBlockedUser,
    blockActionPending,
    blockFeedback,
    blockError,
    canManageBlock,
    isPostLikePending,
    friendsCount,
    confirmAction,
    confirmMessage,
    confirmActionPending,
    confirmError,
    shareCountLookup,
    userPosts,
    profilePostsLoading: profilePostPagination.loading,
    profilePostsLoadingMore: profilePostPagination.loadingMore,
    profilePostsHasMore: profilePostPagination.hasMore,
    loadMoreProfilePosts: profilePostPagination.loadMore,
    backLabel,
    postRefs,
    commentRefs,
    setCommentText,
    setReplyText,
    handleCloseUserProfile,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    handleFollowButtonClick,
    handleFriendButtonClick,
    handleBlockButtonClick,
    closeConfirmModal,
    handleConfirmAction,
    handleMessageClick,
    handleOpenExpandedMedia,
    handleCloseExpandedMedia,
    handleLikePost,
    handleToggleComments,
    handleOpenShare,
    handleOpenInfo,
    handleLikeComment,
    handleToggleReply,
    handleLikeReply,
    handleSubmitReply,
    handleSubmitComment,
  };
}

